import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  DataFhirAdapter,
  DataFhirCategorySetting,
  DataFhirModel,
} from '../../data.model';
import { DictionariesCollectionService } from '../../dictionaries/dictionaries-collection.service';
import { CategoryElement } from '../../partial/categories.model';

export interface AddQuestionnaireEntry
  extends DataFhirModel<fhir4.Questionnaire | fhir4.ServiceRequest> {
  type: string;
  name: string;
  status: string[];
  meta: any;
  description: string;
  classification: string[];
  version: string;
}

@Injectable({
  providedIn: 'root',
})
export class AddQuestionnaireEntryAdapter extends DataFhirAdapter<AddQuestionnaireEntry> {
  categorySettings: DataFhirCategorySetting<AddQuestionnaireEntry>[] = [
    {
      categoryName: 'ageGroup',
      dictionaryIds: ['taxonomytermagegroup'],
    },
    {
      categoryName: 'partner',
      dictionaryIds: ['taxonomytermpartner'],
    },
    {
      categoryName: 'clinicalPicture',
      dictionaryIds: ['taxonomytermclinicalpicture'],
    },
    {
      categoryName: 'symptoms',
      dictionaryIds: ['taxonomytermsymptom'],
    },
    {
      categoryName: 'subjects',
      dictionaryIds: ['taxonomytermsubject'],
    },
    {
      categoryName: 'audience',
      dictionaryIds: ['taxonomytermaudience'],
    },
  ];

  constructor(protected dictionariesCollection: DictionariesCollectionService) {
    super('Questionnaire', dictionariesCollection);
  }

  protected blankResource(): any {
    return {
      resourceType: 'ServiceRequest',
      status: 'active',
      intent: 'original-order',
      authoredOn: formatDate(new Date(), 'yyyy-MM-ddTHH:mm:ss', 'en'),
    };
  }

  fromResource$(observation, detail?): Observable<AddQuestionnaireEntry> {
    return forkJoin([super.fromResource$(observation)]).pipe(
      switchMap(([baseItem]) => {
        const AddQuestionnaireModel = {
          ...observation,
          ...baseItem,
          name: observation['title'],
          //needed for sorting
          type: 'questionnaire',
        };

        return this.getCategories$(observation).pipe(
          map((_categories) => {
            AddQuestionnaireModel['_categories'] = _categories;
            return AddQuestionnaireModel;
          }),
        );
      }),
    );
  }

  toResource(addQuestionnaireEntry: AddQuestionnaireEntry, options): any {
    const { id } = addQuestionnaireEntry;
    const { patientId, practitionerId } = options;

    const observation: fhir4.ServiceRequest = {
      resourceType: 'ServiceRequest',
      status: 'active',
      intent: 'original-order',
      authoredOn: formatDate(new Date(), 'yyyy-MM-ddTHH:mm:ss', 'en'),
      subject: { reference: `Patient/${patientId}` },
      requester: { reference: `Practitioner/${practitionerId}` },
      supportingInfo: [
        {
          reference: `Questionnaire/${id}/_history/${addQuestionnaireEntry?.version}`,
        },
      ],
    };
    if (addQuestionnaireEntry.patientOnly) {
      const coding = this.getPatientOnlySecurityLabel(observation);

      observation['meta'] = {
        security: observation?.meta?.security
          ? [...observation.meta.security, coding]
          : [coding],
      };
    }

    return observation;
  }

  getCategories$(
    detail,
  ): Observable<{ [categoryName: string]: CategoryElement[] }> {
    const codes =
      detail?.useContext?.map(
        (item) => item?.code.code || item?.valueCodeableConcept.coding.code,
      ) || [];

    return forkJoin(
      this.categorySettings.reduce((acc, { categoryName }) => {
        return {
          ...acc,
          [categoryName]: this.getCategoryOptionsList$(categoryName).pipe(
            take(1),
            map((options) =>
              options.filter((option) => codes.includes(option.id)),
            ),
          ),
        };
      }, {}),
    );
  }
}
